import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { Preparing, isPreparing } from "@/components/Preparing";
import { presalesStatus, presalesNavFor } from "@/lib/presales";
import { Phone, ExternalLink } from "lucide-react";

export function PresalesTabs({ active }) {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  useEffect(() => { presalesStatus().then(setS); }, []);
  return (
    <div className="flex gap-1 mb-4 flex-wrap" data-testid="presales-tabs">
      {presalesNavFor(s).map(([l, to]) => <button key={to} onClick={() => navigate(to)} data-testid={`presales-tab-${l.toLowerCase().replace(/\s+/g, "-")}`}
        className={`px-3 py-1.5 rounded-full text-xs border ${active === to ? "bg-[#333333] text-white border-[#333333]" : "bg-white text-[#6B7280] border-[#E2E2E2] hover:border-[#333333]"}`}>{l}</button>)}
    </div>
  );
}

const fmtD = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—");

function Row({ r, navigate }) {
  const [open, setOpen] = useState(false);
  return (
    <tr className="border-b border-[#F7F7F7] align-top text-xs" data-testid={`presales-row-${r.case_id}`}>
      <td className="py-2 px-2 whitespace-nowrap"><button onClick={() => navigate(`/cases/${r.record_id}`)} className="font-mono font-semibold text-[#E2521B] hover:underline">{r.case_id}</button></td>
      <td className="py-2 px-2 min-w-[140px]">{r.name || "—"}{r.never_called && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#F5B71D]/20 text-[#92600a]" data-testid={`never-called-${r.case_id}`}>Never called</span>}</td>
      <td className="py-2 px-2 whitespace-nowrap">{(r.phones || []).length === 0 ? "—" : r.phones.map((p) => <a key={p.tel} href={`tel:${p.tel}`} data-testid={`tel-${r.case_id}`} className="inline-flex items-center gap-1 mr-1 px-2 py-0.5 rounded-full border border-[#E2E2E2] hover:border-[#159A50] hover:text-[#159A50]"><Phone size={11} />{p.display}</a>)}</td>
      <td className="py-2 px-2 whitespace-nowrap"><a href={`tel:${r.phones?.[0]?.tel}`} className={`text-xs ${r.loan?.state === "valid" ? "" : "text-[#CA1212]"}`} data-testid={`dial-loan-${r.case_id}`}>{r.loan?.text}</a></td>
      <td className="py-2 px-2">{r.consult_by || "—"}</td>
      <td className="py-2 px-2 whitespace-nowrap">{fmtD(r.last_conversation)}{r.days_ago != null && <span className="text-[#6B7280]"> · {r.days_ago}d ago</span>}</td>
      <td className="py-2 px-2 whitespace-nowrap">{fmtD(r.follow_up)}</td>
      <td className="py-2 px-2 whitespace-nowrap">{fmtD(r.consult_date)}</td>
      <td className="py-2 px-2">{r.status || "—"}</td>
      <td className="py-2 px-2">{r.call_status || "—"}</td>
      <td className="py-2 px-2 max-w-[320px] text-[#6B7280]">
        {(r.notes || []).length === 0 ? "—" : <div className={open ? "" : "line-clamp-2"} onClick={() => setOpen(!open)} title="Click to expand">{r.notes.map((n, i) => <div key={i}>{n}</div>)}</div>}
      </td>
      <td className="py-2 px-2 whitespace-nowrap"><button onClick={() => navigate(`/cases/${r.record_id}`)} data-testid={`open-case-${r.case_id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-[#E2E2E2] hover:border-[#333333]"><ExternalLink size={11} /> Open case</button></td>
    </tr>
  );
}

export default function Presales() {
  const navigate = useNavigate();
  const [list, setList] = useState("fu_due");
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  const [ms, setMs] = useState(null);
  useEffect(() => {
    const t = performance.now();
    api.get(`/presales/dialling?list=${list}&page=${page}`).then((r) => { setD(r.data); setMs(Math.round(performance.now() - t)); }).catch((e) => setD({ error: e?.response?.status === 404 ? "Pre-Sales is switched off." : "Could not load" }));
  }, [list, page]);
  if (!d) return <Layout><div className="p-6 text-sm text-[#6B7280]">Loading…</div></Layout>;
  if (d.error) return <Layout><div className="p-6 text-sm text-[#CA1212]" data-testid="presales-error">{d.error}</div></Layout>;
  if (isPreparing(d)) return <Layout><Preparing section="Pre-Sales lists" /></Layout>;
  const pages = Math.max(1, Math.ceil(d.total / 50));
  return (
    <Layout>
      <div className="p-5 max-w-[1500px]" data-testid="presales-dialling">
        <PresalesTabs active="/presales" />
        <h1 className="text-xl font-semibold text-[#333333]">Pre-Sales · Dialling</h1>
        <div className="text-xs text-[#92600a] bg-[#F5B71D]/10 border border-[#F5B71D]/30 rounded-lg px-3 py-2 mt-2 inline-block" data-testid="presales-readonly-note">Editing comes next week — update leads in RM Desk for now.</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
          {d.lists.map((l) => (
            <button key={l.key} onClick={() => { setList(l.key); setPage(1); }} data-testid={`presales-counter-${l.key}`}
              className={`text-left rounded-2xl border p-3 ${list === l.key ? "border-[#333333] bg-[#333333] text-white" : "border-[#E2E2E2] bg-white hover:border-[#333333]"}`}>
              <div className="text-2xl font-semibold" data-testid={`presales-count-${l.key}`}>{d.counts[l.key]}</div>
              <div className={`text-[11px] ${list === l.key ? "text-white/80" : "text-[#6B7280]"}`}>{l.label}</div>
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between mt-4 text-xs text-[#6B7280]">
          <span data-testid="presales-list-total">{d.total} lead{d.total === 1 ? "" : "s"} · showing {d.rows.length} · page {page}/{pages}</span>
          <span>snapshot {new Date(d._snapshot_at).toLocaleTimeString("en-IN")}{ms != null ? ` · loaded in ${ms} ms` : ""}</span>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-[#E2E2E2] bg-white mt-2">
          <table className="w-full">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-[#6B7280] bg-[#F7F7F7]">
              {["Case", "Name", "Phone", "Loan amount", "Consultation by", "Last conversation", "Follow-up", "Consultation date", "Purchase status", "Call status", "Notes", ""].map((h, i) => <th key={i} className="py-2 px-2 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {d.rows.length === 0 && <tr><td colSpan={12} className="py-8 text-center text-sm text-[#6B7280]">Nothing in this list.</td></tr>}
              {d.rows.map((r) => <Row key={r.record_id} r={r} navigate={navigate} />)}
            </tbody>
          </table>
        </div>
        {pages > 1 && <div className="flex gap-2 mt-3 text-xs">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 rounded-full border disabled:opacity-40" data-testid="presales-prev">Previous</button>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-3 py-1 rounded-full border disabled:opacity-40" data-testid="presales-next">Next 50</button>
        </div>}
      </div>
    </Layout>
  );
}
